import React from 'react'

import uniqueId from 'lodash/uniqueId'

import { axios } from '../services/axios'

import type {
  FileUpload,
  FileUploadContextData,
  ProviderProps,
  UploadFileAxiosReturn,
} from '../helpers/types/context/files.types'

export const initialState: FileUpload = {
  id: '',
  name: '',
  origin: '',
  uploaded: false,
  progress: false,
  file: null,
  percentage: 0,
  error: false,
  url: '',
}

export const FileUploadContext = React.createContext<FileUploadContextData>(
  {} as FileUploadContextData
)

export function FileUploadProvider({ children }: ProviderProps) {
  const [uploadedFile, setUploadedFile] =
    React.useState<FileUpload>(initialState)

  async function processUpload(
    file: FileUpload,
    origin: string
  ): Promise<void> {
    try {
      const data = new FormData()

      data.append('file', file.file!, file.name)

      const response = await axios().post<UploadFileAxiosReturn>(
        '/upload-file',
        data,
        {
          onUploadProgress: progressEvent => {
            const percentage = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )

            setUploadedFile({
              ...uploadedFile,
              progress: true,
              percentage,
            })
          },
        }
      )

      setUploadedFile({
        ...uploadedFile,
        origin,
        id: response.data.key,
        url: response.data.url,
        uploaded: true,
        progress: false,
      })
    } catch (err) {
      setUploadedFile({
        ...uploadedFile,
        progress: false,
        error: true,
      })
    }
  }

  async function uploadFile(file: File, origin: string): Promise<void> {
    const newFileUpload: FileUpload = {
      id: uniqueId(),
      name: file.name,
      origin,
      uploaded: false,
      progress: true,
      file,
      percentage: 0,
      error: false,
      url: '',
    }

    setUploadedFile(newFileUpload)

    await processUpload(newFileUpload, origin)
  }

  async function deleteFile(key: string): Promise<void> {
    await axios().delete(`/upload-file/${key}`)
    reset()
  }

  function reset(): void {
    setUploadedFile(initialState)
  }

  return (
    <FileUploadContext.Provider
      value={{ uploadedFile, uploadFile, deleteFile, reset }}
    >
      {children}
    </FileUploadContext.Provider>
  )
}
